import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Optional } from 'utility-types';
import { QueryKey } from '@/Config/api/queryKey';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type Props = {
  form: UseFormReturn<any>;
  reset: () => void; // rest of iframe widget
};

/** Make relative URLs absolute so the cross-origin widget iframe can load them. */
export const toAbsoluteUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:'))
    return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

/** Map "" to null for nullable string fields so the form's clear gesture
 * matches the schema's NonEmptyStr | None contract. */
const normalizeNullableStrings = (data: Partial<WidgetSettingsDto>): Partial<WidgetSettingsDto> => {
  const result: any = { ...data };
  if (result.welcome_message === '') result.welcome_message = null;
  if (result.header === '') result.header = null;
  return result;
};

export const useUpdateWidgetSettings = ({ form, reset }: Props) => {
  const { t } = useTranslation('website');
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();

  const { setConfiguration, refreshConfiguration } = usePreview();

  // Widget-side fields derived from a single dashboard field; every push site must replay them
  // together or the preview keeps a derived value the raw field no longer implies.
  const pushDerivedFields = (key: string, value: unknown): void => {
    if (key === 'avatar_image') {
      const avatarUrl = toAbsoluteUrl(value as string | null | undefined);
      setConfiguration('avatar_url', avatarUrl);
      setConfiguration('bubble_icon_url', avatarUrl);
    }
    if (key === 'welcome_message') {
      setConfiguration('welcome_messages', value ? [value] : []);
    }
    if (key === 'background_disclaimer_text') {
      setConfiguration('background_text', value ?? '');
    }
  };

  // Push every form change to the preview iframe instantly (no debounce).
  // Maps dashboard field names to widget ConfigurationDto field names where they differ.
  const formValues = useWatch({ control: form.control });
  const prevValuesRef = useRef<Record<string, any>>({});
  useEffect(() => {
    const prev = prevValuesRef.current;
    // Deep-equal so nested objects like `widget_theme_custom` don't re-emit
    // on every keystroke in unrelated fields (form state replaces the whole
    // object reference on each change, so `!==` would always be true).
    const changed = Object.entries(formValues).filter(([key, value]) => !isEqual(prev[key], value));

    changed.forEach(([key, value]) => {
      setConfiguration(key, value);
      pushDerivedFields(key, value);
    });

    prevValuesRef.current = { ...formValues };
  }, [formValues, setConfiguration]);

  const mutation = useMutation<
    WidgetSettingsDto,
    unknown,
    Optional<WidgetSettingsDto>,
    { previousValues: Record<string, unknown> }
  >({
    mutationKey: ['widget-settings'],
    // Serialize same-key saves so a fast double-change of one field can't run two PATCHes concurrently and have the older resolve last, re-asserting (and pinning) a stale value.
    scope: { id: 'widget-settings' },
    onMutate: (updatedData) => {
      const prevSettings = queryClient.getQueryData<WidgetSettingsDto>(['widget-settings']);
      // Snapshot the pre-save value of each changed field so onError can restore the preview if the PATCH fails.
      const previousValues = Object.fromEntries(
        Object.keys(updatedData).map((key) => [key, (prevSettings as any)?.[key]]),
      );

      queryClient.setQueryData(['widget-settings'], (prevData: WidgetSettingsDto) => ({
        ...prevData,
        ...updatedData,
      }));

      Object.entries(updatedData).forEach(([key, value]) => {
        setConfiguration(key, value);
        pushDerivedFields(key, value);
      });

      return { previousValues };
    },
    onSuccess: (data, variables) => {
      // Re-push only the fields this PATCH sent, and only to catch server normalization: skip any key
      // the user has re-edited while the PATCH was in flight (`onMutate` runs before the `scope` gate,
      // so the newer value is already on screen and re-asserting `data` would visibly revert it), and
      // any key the server didn't echo (input-only keys like `use_default_avatar` would push undefined).
      const currentValues = form.getValues();
      Object.keys(variables).forEach((key) => {
        if (!(key in data)) return;
        const isStillCurrent =
          !(key in currentValues) ||
          isEqual(currentValues[key], (variables as Record<string, unknown>)[key]);
        if (!isStillCurrent) return;

        const serverValue = (data as Record<string, unknown>)[key];
        setConfiguration(key, serverValue);
        pushDerivedFields(key, serverValue);
      });
      if ('avatar_image' in data) {
        // `use_default_avatar` is input-only, so the loop above skips it while the server still
        // resolves a new avatar_image; push the derived URLs from the response either way.
        if (!('avatar_image' in variables)) {
          pushDerivedFields('avatar_image', data.avatar_image);
        }
        // Sync the cache to the server avatar so the form value AND its dirty baseline reflect it
        // (the form's `values` come from this query). Without this the baseline goes stale after a
        // "Use default", and a following Delete sets avatar_image back to a value equal to the stale
        // baseline, so react-hook-form sees no change and the delete PATCH never fires.
        queryClient.setQueryData(['widget-settings'], (prev: WidgetSettingsDto) => ({
          ...prev,
          avatar_image: data.avatar_image ?? '',
        }));
      }

      // Failsafe: tell the widget to refetch config from the server.
      // If the instant postMessage updates above worked, this is a no-op.
      // If they were lost (iframe reloading, listener not ready), this catches up.
      refreshConfiguration();

      void queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });

      capture(PostHogEventEnum.WidgetAppearanceChanged, {
        changed_fields: Object.keys(variables),
      });
    },
    onError: (error: any, _variables, context) => {
      const previousValues = context?.previousValues;
      if (previousValues) {
        // Restore the dashboard cache and revert the preview to the pre-save values so a failed save doesn't leave a stale optimistic value shielded in the widget.
        queryClient.setQueryData(['widget-settings'], (widgetSettings: WidgetSettingsDto) => ({
          ...widgetSettings,
          ...previousValues,
        }));
        Object.entries(previousValues).forEach(([key, value]) => {
          setConfiguration(key, value);
          pushDerivedFields(key, value);
        });
        refreshConfiguration();
      }

      toast.error(
        error.response?.data?.errors?.root?.[0]?.message || t('appearance.form.error.common'),
      );
    },
  });

  const handleMutation = (data: Partial<WidgetSettingsDto>) =>
    mutation.mutate(normalizeNullableStrings(data));

  // TODO: backend should add default values for prechat_message_text
  useAutoSubmit<WidgetSettingsDto>(form, (data) => {
    // widget_theme_custom is persisted in a separate table with full-replace
    // PATCH semantics; the dirty-fields helper would otherwise send a partial
    // nested diff and wipe the rest of the custom theme. Always send the full
    // object from form state.
    const payload: Partial<WidgetSettingsDto> =
      'widget_theme_custom' in data
        ? { ...data, widget_theme_custom: form.getValues('widget_theme_custom') }
        : data;

    if (payload.prechat_active === true) {
      const prechatMessageText =
        payload.prechat_message_text ?? form.getValues('prechat_message_text');

      mutation.mutate(
        normalizeNullableStrings({
          ...payload,
          prechat_message_text: payload.prechat_message_text || prechatMessageText,
        }),
      );

      reset();
      return;
    }

    // Only reset iframe when the prechat toggle itself changes (off); text field
    // changes are pushed to the preview via postMessage and don't need a reload.
    if ('prechat_active' in payload) {
      reset();
    }

    mutation.mutate(normalizeNullableStrings(payload));
  });

  return {
    handleSubmitAll: (data: Partial<WidgetSettingsDto>) =>
      mutation.mutate(normalizeNullableStrings(data)),
    handleMutation,
  };
};
